import React, { useState, useEffect } from 'react';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useGetExamQuestions } from './properties/useGetExamQuestions';
import TextQuestion from './components/TextQuestions';
import MultipleChoiceQuestion from './components/MultipleChoiceQuestion';
import MultiSelectQuestion from './components/MultiSelectQuestion';
import { useGetAssessment } from './properties/useGetAssessment';
import { FetchStatus, IEntity } from '../../../base/types';
import { IFieldValue } from '../../../services/models';
import { useAuth } from '../../../../modules/auth';
import { useCreateMutation, useUpdateMutation } from '../../../services/data/tableDataApi';
import { useDataContext } from '../../page/context';
import { useParams } from 'react-router-dom';
import { ProgressIndicator } from '../../../widgets';
import { useGetExamMarks } from './properties/useGetExamMarks';
import { useGetCountDown } from './components/useGetCountDown';
import { QuestionState } from './properties/assessment';
import { GeneralAwareness } from './components/GeneralAwareness';
interface FormData {
  [key: string]: string | string[];
}

const initialValues: FormData = {};

export const ExamQuestionsComponent = () => {
  const { examId } = useParams<{ examId?: string }>();
  const { questions } = useGetExamQuestions(examId || '');
  const { examMarksId } = useGetExamMarks(examId || '');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [questionStates, setQuestionStates] = useState<QuestionState[]>([]);
  const { assessments } = useGetAssessment();
  const exam = assessments.find(assessment => assessment.assessmentId === examId);
  const [filteredQuestions, setFilteredQuestions] = useState(questions);
  const { user, siteId } = useAuth();
  const [modifyRecord] = useUpdateMutation();
  const [insertRecord] = useCreateMutation();
  const { setData } = useDataContext();
  const [formData, setFormData] = useState<FormData>(initialValues);
  const [loading, setLoading] = useState<boolean>(false);
  const { getFormattedTime, remainingTime } = useGetCountDown(exam)

  const updateQuestionState = (index: number, updates: Partial<QuestionState>) => {
    setQuestionStates(prevStates =>
      prevStates.map((state, i) =>
        i === index ? { ...state, ...updates } : state
      )
    );
  };

  const handleQuestionVisit = (index: number) => {
    const currentState = questionStates[index];
    if (!currentState.visited) {
      updateQuestionState(index, { visited: true });
    }
  };

  const handleClearResponse = () => {
    const currentIndex = currentQuestionIndex;
    updateQuestionState(currentIndex, { selectedOption: null, markedForReview: false });
    setFormData(prevData => ({ ...prevData, [currentQuestion.questionId]: '' }));
  };

  const handleMarkForReview = () => {
    updateQuestionState(currentQuestionIndex, { markedForReview: true });
    handleNextQuestion();
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    const index = filteredQuestions.findIndex(q => q.questionId === questionId);
    const state = questionStates[index];

    const wasMarkedForReview = state?.markedForReview;
    const isAnswered = answer !== '';
    const wasAnswered = state?.selectedOption !== null;

    updateQuestionState(index, {
      selectedOption: answer,
      markedForReview: wasMarkedForReview && !isAnswered ? true : false
    });

    if (wasAnswered && !wasMarkedForReview && isAnswered) {
      // Answered previously and now marked for review
      // Adjust counts if needed
    } else if (isAnswered) {
      // Question answered
      updateQuestionState(index, { markedForReview: false });
    }
  };

  useEffect(() => {
    if (questions) {
      const filtered = questions.filter(question => question.questionType); // Example filter by question type
      setFilteredQuestions(filtered);

      const initialData: FormData = {};
      filtered.forEach(question => {
        initialData[question.questionId] = question.questionType === 365970000 && question.mcqType === 365970000 ? [] : '';
      });
      setFormData(initialData);
    }
    // eslint-disable-next-line
  }, [questions]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    handleAnswerChange(name, value);
  };

  const handleMultiSelectChange = (name: string, selectedValues: string[]) => {
    setFormData({
      ...formData,
      [name]: selectedValues,
    });
    handleAnswerChange(name, selectedValues.join(','));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const responsePromises = filteredQuestions
        .filter(question => formData[question.questionId]?.length > 0)
        .map(async question => {
          const responseValue = formData[question.questionId];

          if (question.questionType === 365970001) {
            return saveDescriptiveResponse(question.questionId, responseValue as string);
          } else if (question.mcqType === 365970000) {
            return saveMultiSelectResponse(question.questionId, responseValue as string[]);
          } else if (question.mcqType === 365970001) {
            return saveMultipleChoiceResponse(question.questionId, responseValue as string);
          } else {
            return null;
          }
        });

      const responses = await Promise.all(responsePromises);
      setData({ type: "Card", result: responses, status: FetchStatus.Success });
    } catch (error) {
      console.error("Submission Error:", error);
    }
  };

  const saveMultiSelectResponse = async (questionId: string, selectedValues: string[]): Promise<IEntity[]> => {
    setLoading(true);

    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);

    if (examMarkRecord) {
      const insertPromises = selectedValues.map(selectedValue => {
        const fields: IFieldValue[] = [
          { field: "edtec365_choice", value: selectedValue, validate: true },
          { field: "edtec365_exammarks", value: examMarkRecord.examMarksId, validate: true },
        ];

        return insertRecord({
          portalId: siteId || '',
          userId: user?.contactId,
          tableId: "edtec365_choiceresponse",
          data: fields,
          triggers: true,
        })
          .unwrap()
          .catch(err => {
            const message = err.data?.message || 'An error occurred while saving the multi-select response. Please try again later.';
            console.error("Multi-Select Response Error:", message);
            return Promise.reject(message);
          });
      });

      const responseRecords = await Promise.all(insertPromises);
      setLoading(false);
      return responseRecords;
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      setLoading(false);
      return Promise.resolve([] as IEntity[]);
    }
  };

  const saveMultipleChoiceResponse = (questionId: string, selectedValue: string): Promise<IEntity> => {
    setLoading(true);

    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);

    if (examMarkRecord) {
      const fields: IFieldValue[] = [
        { field: "edtec365_choice", value: selectedValue, validate: true },
        { field: "edtec365_exammarks", value: examMarkRecord.examMarksId, validate: true },
      ];

      return insertRecord({
        portalId: siteId || '',
        userId: user?.contactId,
        tableId: "edtec365_choiceresponse",
        data: fields,
        triggers: true,
      })
        .unwrap()
        .catch(err => {
          const message = err.data?.message || 'An error occurred while saving the multiple-choice response. Please try again later.';
          console.error("Multiple Choice Response Error:", message);
          return Promise.reject(message);
        })
        .finally(() => setLoading(false));
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      return Promise.resolve({} as IEntity);
    }
  };

  const saveDescriptiveResponse = (questionId: string, responseValue: string): Promise<IEntity> => {
    setLoading(true);

    const examMarkRecord = examMarksId.find(mark => mark.questionMappingId === questionId);

    if (examMarkRecord) {
      const fields: IFieldValue[] = [
        { field: "edtec365_questionresponse", value: responseValue, validate: true },
      ];

      return modifyRecord({
        portalId: siteId || '',
        userId: user?.contactId,
        tableId: "slcm_exammarks",
        data: fields,
        sysId: examMarkRecord.examMarksId,
        triggers: true,
      })
        .unwrap()
        .catch(err => {
          const message = err.data?.message || 'An error occurred while saving the descriptive response. Please try again later.';
          console.error("Descriptive Response Error:", message);
          return Promise.reject(message);
        })
        .finally(() => setLoading(false));
    } else {
      console.warn(`No exam mark found for questionId: ${questionId}`);
      return Promise.resolve({} as IEntity);
    }
  };

  useEffect(() => {
    if (filteredQuestions.length > 0) {
      setQuestionStates(filteredQuestions.map(() => ({
        visited: false,
        markedForReview: false,
        selectedOption: null,
      })));
    }
    // eslint-disable-next-line
  }, [filteredQuestions]);

  useEffect(() => {
    if (filteredQuestions.length > 0) {
      const updatedStates = filteredQuestions.map((_, index) => ({
        visited: questionStates[index]?.visited || false,
        markedForReview: questionStates[index]?.markedForReview || false,
        selectedOption: formData[filteredQuestions[index].questionId] || null,
      }));
      setQuestionStates(updatedStates);
    }
    // eslint-disable-next-line
  }, [formData, filteredQuestions]);

  const handleNextQuestion = () => {
    setQuestionStates(prevStates => {
      const updatedStates = [...prevStates];
      if (updatedStates[currentQuestionIndex]) {
        updatedStates[currentQuestionIndex] = {
          ...updatedStates[currentQuestionIndex],
          visited: true,
        };
      }
      return updatedStates;
    });
    setCurrentQuestionIndex(prevIndex => (prevIndex < filteredQuestions.length - 1 ? prevIndex + 1 : prevIndex));
  };

  const getColorForQuestion = (index: number) => {
    const questionState = questionStates[index];
    if (!questionState) return '#2196f3'; // Default blue for not visited

    if (questionState.selectedOption && questionState.markedForReview) return '#8a2be2'; // Purple for answered and marked for review
    if (questionState.selectedOption) return '#4caf50'; // Green for answered
    if (questionState.markedForReview) return '#ffeb3b'; // Yellow for marked for review
    if (questionState.visited) return '#f44336'; // Red for visited but not answered
    return '#2196f3'; // Default blue for not visited
  };

  const currentQuestion = filteredQuestions[currentQuestionIndex];

  return (
    <Box >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: '#007bff', padding: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Exam Name on the Left */}
              <Typography variant="h6" color='white'>
                {exam?.name}
              </Typography>

              {/* Timer with Clock Icon on the Right */}
              <Box textAlign="right" display="flex" alignItems="center">
                <AccessTimeIcon sx={{ color: 'white', marginRight: 1 }} /> {/* Clock icon with some spacing */}
                <Typography variant="h6" color='white'>

                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    marginTop: 0,
                    fontWeight: 'bolder',
                    marginLeft: 0,
                    color: remainingTime <= 120 ? 'red' : 'yellow', // Change color to red if time left is <= 5 minutes
                  }}
                >
                  {getFormattedTime(remainingTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={8}>
            {(currentQuestion && filteredQuestions) && (
              <Paper sx={{ padding: 3, minHeight: '350px', overflowY: 'auto' }}>
                <Typography variant="h6">Question No. {currentQuestion.questionNumber}</Typography>
                <Typography variant="body1" sx={{ marginTop: 2 }}>
                  <label className="form-label">
                    <span dangerouslySetInnerHTML={{ __html: currentQuestion.questionDescription }}></span>
                  </label>
                </Typography>

                {currentQuestion.questionType === 365970001 && (
                  <TextQuestion
                    question={currentQuestion}
                    name={currentQuestion.questionId}
                    value={formData[currentQuestion.questionId] as string}
                    onChange={handleChange}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}


                {(currentQuestion.questionType === 365970000 && currentQuestion.mcqType === 365970001) && (
                  <MultipleChoiceQuestion
                    question={currentQuestion}
                    name={currentQuestion.questionId}
                    value={formData[currentQuestion.questionId] as string}
                    onChange={handleChange}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}

                {(currentQuestion.questionType === 365970000 && currentQuestion.mcqType === 365970000) && (
                  <MultiSelectQuestion
                    question={currentQuestion}
                    value={formData[currentQuestion.questionId] as string[]}
                    onChange={(selectedValues) => handleMultiSelectChange(currentQuestion.questionId, selectedValues)}
                    onBlur={() => handleQuestionVisit(currentQuestionIndex)}
                  />
                )}
              </Paper>
            )}

            <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button disabled={currentQuestionIndex === filteredQuestions.length - 1} variant="outlined" onClick={handleMarkForReview}>Mark for Review & Next</Button>
              <Button variant="outlined" onClick={handleClearResponse}>Clear Response</Button>
              <Button disabled={currentQuestionIndex === filteredQuestions.length - 1} variant="contained" color="primary" onClick={handleNextQuestion}>Save & Next</Button>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Grid item xs={12} mb={2}>
              <GeneralAwareness questions={filteredQuestions} questionStates={questionStates} />
            </Grid>
            <Paper sx={{ padding: 3, maxHeight: 215, overflowY: 'auto' }}>
              <Typography variant="h6">Choose a Question</Typography>
              <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>Select a question to navigate</Typography>

              <Grid container spacing={1}>
                {filteredQuestions.map((question, index) => (
                  <Grid item xs={3} key={question.questionId}>
                    <Box
                      sx={{
                        backgroundColor: getColorForQuestion(index),
                        width: '100%',
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        color: '#fff',
                        textAlign: 'center'
                      }}
                    >
                      <Button
                        variant="text"
                        sx={{ color: '#fff', width: '100%', height: '100%' }}
                        onClick={() => setCurrentQuestionIndex(index)}
                      >
                        {index + 1}
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Button disabled={currentQuestionIndex !== filteredQuestions.length - 1} type='submit' variant="contained" color="success" fullWidth sx={{ marginTop: 3 }}>
                {loading ? (
                  <ProgressIndicator show={true} message='Please wait...' />
                ) : (
                  'Submit'
                )}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
